import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, Handshake, Scale, ShieldCheck } from 'lucide-react';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

/**
 * Public documentation page for the dispute / mediation flow. Linked from
 * pricing tables on lawyer profiles, from the lawyer partnership terms, and
 * from the in-app task disputed state (once the UI lands in Phase 2).
 *
 * This page is documentation — there's no interactive mediation here yet.
 * It exists to set expectations for both clientes and advogados about how
 * a dispute resolves.
 */
export function TermosMediacao() {
  return (
    <MarketingLayout>
      <div className="container-px mx-auto max-w-3xl py-16">
        <Badge variant="accent" className="mb-2">Política</Badge>
        <h1 className="font-serif text-display-lg">Termos de mediação e disputa</h1>
        <p className="text-muted-foreground mt-2">
          Como a Parecer.ai resolve quando cliente e advogado discordam sobre o serviço prestado.
          Vale pra qualquer tarefa contratada na plataforma.
        </p>

        {/* TL;DR */}
        <Card className="mt-8 border-accent/30 bg-accent/[0.04]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Scale className="h-5 w-5 text-accent" /> Em resumo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>
              O pagamento fica em <strong>escrow</strong> no Pagar.me até a entrega ser aprovada
              pelo cliente. Se algo der errado, qualquer um dos lados pode abrir uma{' '}
              <strong>disputa</strong>. Uma pessoa da equipe Parecer.ai analisa a conversa, prazos
              e SLA e decide:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Liberar pro advogado (serviço prestado conforme combinado).</li>
              <li>Reembolsar o cliente (serviço não prestado ou muito aquém do combinado).</li>
              <li>Divisão proporcional (entrega parcial, ambos os lados com razão parcial).</li>
            </ul>
            <p className="text-xs text-muted-foreground pt-2">
              A decisão da Parecer.ai é vinculante para fins de liberação do escrow. Cliente e
              advogado mantêm seus direitos legais independentemente.
            </p>
          </CardContent>
        </Card>

        {/* Quando o cliente pode abrir disputa */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-warning" /> Quando o cliente pode abrir disputa
            </CardTitle>
            <CardDescription>Motivos legítimos previstos.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <span className="text-warning flex-none">→</span>
                <span>
                  <strong>Prazo descumprido.</strong> Advogado não entregou no prazo combinado e
                  não negociou extensão.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-warning flex-none">→</span>
                <span>
                  <strong>SLA de resposta descumprido.</strong> No chat consultivo, advogado
                  responde fora do compromisso anunciado (ex: "respondo em até 24h" e ficou 5 dias
                  sem resposta).
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-warning flex-none">→</span>
                <span>
                  <strong>Escopo não entregue.</strong> Serviço entregue não cobre o que estava na
                  descrição. Ex: cliente pediu revisão de contrato + sugestão de redação alternativa,
                  recebeu só uma lista de "atenção" sem proposta.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-warning flex-none">→</span>
                <span>
                  <strong>Qualidade aquém do razoável.</strong> Erros gritantes, ausência de
                  fundamentação, copy/paste óbvio. Critério subjetivo — a mediação pondera.
                </span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Quando o advogado pode abrir disputa */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Handshake className="h-5 w-5 text-accent" /> Quando o advogado pode abrir disputa
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <span className="text-accent flex-none">→</span>
                <span>
                  <strong>Cliente sumiu após aceitar entrega.</strong> Entrega feita, cliente não
                  aprova nem rejeita por mais de 7 dias. Aprovação automática + liberação.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent flex-none">→</span>
                <span>
                  <strong>Cliente pede escopo fora do combinado.</strong> Cliente exige extras não
                  inclusos na descrição (ex: contratou revisão simples, exige redação do zero).
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent flex-none">→</span>
                <span>
                  <strong>Comportamento abusivo.</strong> Cliente desrespeita, pressiona fora de
                  horário, faz exigências inviáveis. Advogado pode cancelar e a Parecer.ai
                  avalia.
                </span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Como funciona */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-5 w-5 text-accent" /> Fluxo de resolução
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <ol className="space-y-2 list-decimal pl-5">
              <li>
                <strong>Abertura.</strong> Qualquer lado clica em "Abrir disputa" na tarefa,
                descreve o motivo (até 2000 caracteres) e anexa evidências (prints da conversa,
                arquivos entregues, etc).
              </li>
              <li>
                <strong>Resposta do outro lado.</strong> 48 horas para apresentar a versão dele.
                Sem resposta no prazo → mediação decide com o que tiver.
              </li>
              <li>
                <strong>Mediação.</strong> Equipe Parecer.ai lê a thread da tarefa, evidências,
                histórico do advogado e do cliente, e proporciona uma proposta de resolução em
                até <strong>5 dias úteis</strong>.
              </li>
              <li>
                <strong>Aceite ou contestação.</strong> Ambos os lados podem aceitar a proposta
                ou pedir reavaliação (uma vez). Após reavaliação, a decisão é final pra liberação
                do escrow.
              </li>
              <li>
                <strong>Execução.</strong> Parecer.ai aciona o Pagar.me pra liberar/reembolsar
                conforme a decisão. Reembolsos chegam ao cartão/PIX original em até 5 dias úteis.
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Não cobre */}
        <Card className="mt-6 border-border/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-5 w-5 text-muted-foreground" /> Esta mediação não cobre
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              <li>• Erros materiais identificados após a aprovação pelo cliente (responsabilidade civil profissional).</li>
              <li>• Resultados de processos judiciais (advocacia é obrigação de meio, não de fim).</li>
              <li>• Discussões éticas perante a OAB (essas seguem o processo do conselho).</li>
              <li>• Reembolso de pagamentos feitos fora da plataforma.</li>
            </ul>
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="mt-6 bg-primary text-primary-foreground border-0">
          <CardContent className="pt-6 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 flex-none" />
              <div>
                <h3 className="font-serif text-lg">Dúvidas antes de contratar?</h3>
                <p className="text-sm text-primary-foreground/80 mt-0.5">
                  Fale com a gente — preferimos esclarecer antes que disputar depois.
                </p>
              </div>
            </div>
            <Link
              to="/como-funciona"
              className="rounded-md bg-accent text-accent-foreground px-4 py-2 text-sm font-medium hover:bg-accent/90"
            >
              Como funciona
            </Link>
          </CardContent>
        </Card>

        <p className="text-[11px] text-muted-foreground text-center mt-8">
          Política em vigor a partir do MVP. Versões anteriores e atualizações futuras ficam
          arquivadas em /termos-mediacao/historico (em breve).
        </p>
      </div>
    </MarketingLayout>
  );
}
